export interface TimezoneInfo {
  id: string;
  name: string;
  abbr: string;
  offset: number; // UTC offset in hours
  displayName: string;
}

/**
 * IANA timezone to app timezone key mapping
 */
const IANA_TO_TIMEZONE_KEY: Record<string, string> = {
  'America/New_York': 'EST',
  'America/Chicago': 'CST',
  'America/Denver': 'MST',
  'America/Los_Angeles': 'PST',
  'America/Anchorage': 'AKST',
  'Pacific/Honolulu': 'HST',
  'America/Sao_Paulo': 'BRT',
  'America/Argentina/Buenos_Aires': 'ART',
  'America/Santiago': 'CLT',
  'America/Bogota': 'COT',
  'America/Lima': 'PET',
  'America/Caracas': 'VET',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Europe/Berlin': 'CET',
  'Europe/Rome': 'CET',
  'Europe/Madrid': 'CET',
  'Europe/Amsterdam': 'CET',
  'Europe/Brussels': 'CET',
  'Europe/Vienna': 'CET',
  'Europe/Zurich': 'CET',
  'Europe/Lisbon': 'WET',
  'Europe/Athens': 'EET',
  'Europe/Helsinki': 'EET',
  'Europe/Bucharest': 'EET',
  'Europe/Moscow': 'MSK',
  'Europe/Istanbul': 'TRT',
  'Asia/Kolkata': 'IST',
  'Asia/Calcutta': 'IST',
  'Asia/Karachi': 'PKT',
  'Asia/Dhaka': 'BST_BD',
  'Asia/Kathmandu': 'NPT',
  'Asia/Bangkok': 'ICT',
  'Asia/Ho_Chi_Minh': 'ICT',
  'Asia/Jakarta': 'WIB',
  'Asia/Singapore': 'SGT',
  'Asia/Kuala_Lumpur': 'MYT',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Shanghai': 'CST_CN',
  'Asia/Manila': 'PHT',
  'Asia/Seoul': 'KST',
  'Asia/Tokyo': 'JST',
  'Asia/Riyadh': 'AST_AR',
  'Asia/Dubai': 'GST',
  'Asia/Tehran': 'IRST',
  'Asia/Jerusalem': 'IST_IL',
  'Africa/Cairo': 'CAT',
  'Africa/Nairobi': 'EAT',
  'Africa/Lagos': 'WAT',
  'Africa/Johannesburg': 'SAST',
  'Australia/Sydney': 'AEST',
  'Australia/Melbourne': 'AEST',
  'Australia/Adelaide': 'ACST',
  'Australia/Perth': 'AWST',
  'Pacific/Auckland': 'NZST',
  'Pacific/Fiji': 'FJT',
};

/**
 * Detect the user's timezone using browser APIs
 * Falls back to EST if detection fails
 */
export function detectUserTimezone(): TimezoneInfo {
  try {
    const ianaTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Direct IANA lookup
    const key = IANA_TO_TIMEZONE_KEY[ianaTimezone];
    if (key && TIMEZONES[key]) {
      return TIMEZONES[key];
    }
    
    // Fallback: match by UTC offset
    const offsetMinutes = new Date().getTimezoneOffset();
    const offsetHours = -offsetMinutes / 60; // getTimezoneOffset returns inverse
    
    // Prefer common timezones when matching by offset
    const preferredKeys = ['EST', 'CST', 'MST', 'PST', 'GMT', 'CET', 'IST', 'JST', 'AEST', 'SGT', 'HKT', 'KST', 'GST'];
    for (const k of preferredKeys) {
      if (TIMEZONES[k] && TIMEZONES[k].offset === offsetHours) {
        return TIMEZONES[k];
      }
    }
    
    // Any match by offset
    const allKeys = Object.keys(TIMEZONES);
    for (const k of allKeys) {
      if (TIMEZONES[k].offset === offsetHours) {
        return TIMEZONES[k];
      }
    }
  } catch {
    // Detection failed
  }
  
  return TIMEZONES.EST;
}

/**
 * Map timezone IDs to IANA timezone names for DST detection.
 * Only timezones that observe DST are included.
 */
const TIMEZONE_ID_TO_IANA: Record<string, string> = {
  EST: 'America/New_York',
  EDT: 'America/New_York',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  CENTRAL: 'America/Chicago',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  PT: 'America/Los_Angeles',
  AKST: 'America/Anchorage',
  AKDT: 'America/Anchorage',
  GMT: 'Europe/London',
  UK: 'Europe/London',
  BST: 'Europe/London',
  CET: 'Europe/Paris',
  CEST: 'Europe/Paris',
  WET: 'Europe/Lisbon',
  WEST: 'Europe/Lisbon',
  EET: 'Europe/Athens',
  EEST: 'Europe/Athens',
  CLT: 'America/Santiago',
  IRST: 'Asia/Tehran',
  IST_IL: 'Asia/Jerusalem',
  AEST: 'Australia/Sydney',
  AEDT: 'Australia/Sydney',
  ACST: 'Australia/Adelaide',
  ACDT: 'Australia/Adelaide',
  NZST: 'Pacific/Auckland',
  NZDT: 'Pacific/Auckland',
};

/**
 * Detect if a timezone is currently observing Daylight Saving Time.
 * Compares the current UTC offset against the January (standard) offset.
 */
export function isDSTActive(timezoneId: string): boolean {
  const iana = TIMEZONE_ID_TO_IANA[timezoneId];
  if (!iana) return false;

  const now = new Date();
  const jan = new Date(now.getFullYear(), 0, 1);
  const jul = new Date(now.getFullYear(), 6, 1);

  const getOffset = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'shortOffset',
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    if (!tzPart) return 0;
    // Format like "GMT+1", "GMT-5", "GMT+5:30"
    const match = tzPart.value.match(/GMT([+-]?\d+(?::(\d+))?)?/);
    if (!match) return 0;
    if (!match[1]) return 0;
    const h = parseInt(match[1], 10);
    const m = match[2] ? parseInt(match[2], 10) : 0;
    return h * 60 + (h < 0 ? -m : m);
  };

  const janOffset = getOffset(jan);
  const julOffset = getOffset(jul);
  const nowOffset = getOffset(now);

  // Standard offset is the one closer to UTC (smaller absolute value for northern hemisphere)
  // or just whichever is different from now
  const standardOffset = Math.min(janOffset, julOffset);
  return nowOffset !== standardOffset;
}

/**
 * Detect if an IANA timezone string is currently observing DST.
 * Works directly with IANA strings like "America/New_York".
 */
export function isDSTActiveByIANA(iana: string): boolean {
  try {
    const now = new Date();
    const jan = new Date(now.getFullYear(), 0, 1);
    const jul = new Date(now.getFullYear(), 6, 1);

    const getOffset = (date: Date) => {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        timeZoneName: 'shortOffset',
      }).formatToParts(date);
      const tzPart = parts.find((p) => p.type === 'timeZoneName');
      if (!tzPart) return 0;
      const match = tzPart.value.match(/GMT([+-]?\d+(?::(\d+))?)?/);
      if (!match) return 0;
      if (!match[1]) return 0;
      const h = parseInt(match[1], 10);
      const m = match[2] ? parseInt(match[2], 10) : 0;
      return h * 60 + (h < 0 ? -m : m);
    };

    const janOffset = getOffset(jan);
    const julOffset = getOffset(jul);
    const nowOffset = getOffset(now);

    if (janOffset === julOffset) return false;
    const standardOffset = Math.min(janOffset, julOffset);
    return nowOffset !== standardOffset;
  } catch {
    return false;
  }
}

/**
 * Get the current actual UTC offset (in hours) for a timezone,
 * accounting for DST. Falls back to the static offset if no IANA mapping exists.
 */
export function getCurrentOffset(timezoneId: string): number {
  const iana = TIMEZONE_ID_TO_IANA[timezoneId];
  if (!iana) {
    return TIMEZONES[timezoneId]?.offset ?? 0;
  }

  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    timeZoneName: 'shortOffset',
  }).formatToParts(now);
  const tzPart = parts.find((p) => p.type === 'timeZoneName');
  if (!tzPart) return TIMEZONES[timezoneId]?.offset ?? 0;

  const match = tzPart.value.match(/GMT([+-]?\d+(?::(\d+))?)?/);
  if (!match || !match[1]) return 0;
  const h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  return h + (h < 0 ? -m : m) / 60;
}

/**
 * DST abbreviation pairs: standard → daylight
 */
const DST_ABBR_MAP: Record<string, string> = {
  EST: 'EDT', CST: 'CDT', MST: 'MDT', PST: 'PDT',
  AKST: 'AKDT', GMT: 'BST', CET: 'CEST', WET: 'WEST',
  EET: 'EEST', AEST: 'AEDT', ACST: 'ACDT', NZST: 'NZDT',
  CLT: 'CLST', IRST: 'IRDT', IST_IL: 'IDT',
};

/**
 * Returns a formatted abbreviation string showing both standard and DST forms,
 * with the currently active one bolded.
 * E.g. when DST is active for EST: { standard: 'EST', daylight: 'EDT', isDST: true }
 * Non-DST timezones return just { standard: abbr, daylight: null, isDST: false }
 */
export function getDSTAbbrInfo(timezoneId: string): {
  standard: string;
  daylight: string | null;
  isDST: boolean;
  activeAbbr: string;
} {
  const tz = TIMEZONES[timezoneId];
  if (!tz) return { standard: timezoneId, daylight: null, isDST: false, activeAbbr: timezoneId };

  const daylightAbbr = DST_ABBR_MAP[timezoneId];
  if (!daylightAbbr) {
    return { standard: tz.abbr, daylight: null, isDST: false, activeAbbr: tz.abbr };
  }

  const dst = isDSTActive(timezoneId);
  return {
    standard: tz.abbr,
    daylight: daylightAbbr,
    isDST: dst,
    activeAbbr: dst ? daylightAbbr : tz.abbr,
  };
}


export const TIMEZONES: Record<string, TimezoneInfo> = {
  // North America
  EST: { id: 'EST', name: 'Eastern Standard Time', abbr: 'EST', offset: -5, displayName: 'New York (EST)' },
  EDT: { id: 'EDT', name: 'Eastern Daylight Time', abbr: 'EDT', offset: -4, displayName: 'New York (EDT)' },
  CST: { id: 'CST', name: 'Central Standard Time', abbr: 'CST', offset: -6, displayName: 'Chicago (CST)' },
  CDT: { id: 'CDT', name: 'Central Daylight Time', abbr: 'CDT', offset: -5, displayName: 'Chicago (CDT)' },
  MST: { id: 'MST', name: 'Mountain Standard Time', abbr: 'MST', offset: -7, displayName: 'Denver (MST)' },
  MDT: { id: 'MDT', name: 'Mountain Daylight Time', abbr: 'MDT', offset: -6, displayName: 'Denver (MDT)' },
  PST: { id: 'PST', name: 'Pacific Standard Time', abbr: 'PST', offset: -8, displayName: 'Los Angeles (PST)' },
  PDT: { id: 'PDT', name: 'Pacific Daylight Time', abbr: 'PDT', offset: -7, displayName: 'Los Angeles (PDT)' },
  PT: { id: 'PT', name: 'Pacific Time', abbr: 'PT', offset: -8, displayName: 'Pacific Time' },
  AKST: { id: 'AKST', name: 'Alaska Standard Time', abbr: 'AKST', offset: -9, displayName: 'Anchorage (AKST)' },
  AKDT: { id: 'AKDT', name: 'Alaska Daylight Time', abbr: 'AKDT', offset: -8, displayName: 'Anchorage (AKDT)' },
  HST: { id: 'HST', name: 'Hawaii Standard Time', abbr: 'HST', offset: -10, displayName: 'Honolulu (HST)' },
  CENTRAL: { id: 'CENTRAL', name: 'Central Time', abbr: 'CT', offset: -6, displayName: 'Central Time' },
  
  // South America
  BRT: { id: 'BRT', name: 'Brasilia Time', abbr: 'BRT', offset: -3, displayName: 'São Paulo (BRT)' },
  ART: { id: 'ART', name: 'Argentina Time', abbr: 'ART', offset: -3, displayName: 'Buenos Aires (ART)' },
  CLT: { id: 'CLT', name: 'Chile Standard Time', abbr: 'CLT', offset: -4, displayName: 'Santiago (CLT)' },
  COT: { id: 'COT', name: 'Colombia Time', abbr: 'COT', offset: -5, displayName: 'Bogotá (COT)' },
  PET: { id: 'PET', name: 'Peru Time', abbr: 'PET', offset: -5, displayName: 'Lima (PET)' },
  VET: { id: 'VET', name: 'Venezuela Time', abbr: 'VET', offset: -4, displayName: 'Caracas (VET)' },
  
  // Europe
  GMT: { id: 'GMT', name: 'Greenwich Mean Time', abbr: 'GMT', offset: 0, displayName: 'London (GMT)' },
  UTC: { id: 'UTC', name: 'Coordinated Universal Time', abbr: 'UTC', offset: 0, displayName: 'UTC' },
  UK: { id: 'UK', name: 'UK Time', abbr: 'UK', offset: 0, displayName: 'London (UK)' },
  BST: { id: 'BST', name: 'British Summer Time', abbr: 'BST', offset: 1, displayName: 'London (BST)' },
  CET: { id: 'CET', name: 'Central European Time', abbr: 'CET', offset: 1, displayName: 'Paris (CET)' },
  CEST: { id: 'CEST', name: 'Central European Summer Time', abbr: 'CEST', offset: 2, displayName: 'Paris (CEST)' },
  WET: { id: 'WET', name: 'Western European Time', abbr: 'WET', offset: 0, displayName: 'Lisbon (WET)' },
  WEST: { id: 'WEST', name: 'Western European Summer Time', abbr: 'WEST', offset: 1, displayName: 'Lisbon (WEST)' },
  EET: { id: 'EET', name: 'Eastern European Time', abbr: 'EET', offset: 2, displayName: 'Athens (EET)' },
  EEST: { id: 'EEST', name: 'Eastern European Summer Time', abbr: 'EEST', offset: 3, displayName: 'Athens (EEST)' },
  MSK: { id: 'MSK', name: 'Moscow Standard Time', abbr: 'MSK', offset: 3, displayName: 'Moscow (MSK)' },
  
  // Asia
  IST: { id: 'IST', name: 'India Standard Time', abbr: 'IST', offset: 5.5, displayName: 'Mumbai (IST)' },
  PKT: { id: 'PKT', name: 'Pakistan Standard Time', abbr: 'PKT', offset: 5, displayName: 'Karachi (PKT)' },
  BST_BD: { id: 'BST_BD', name: 'Bangladesh Standard Time', abbr: 'BST', offset: 6, displayName: 'Dhaka (BST)' },
  NPT: { id: 'NPT', name: 'Nepal Time', abbr: 'NPT', offset: 5.75, displayName: 'Kathmandu (NPT)' },
  ICT: { id: 'ICT', name: 'Indochina Time', abbr: 'ICT', offset: 7, displayName: 'Bangkok (ICT)' },
  WIB: { id: 'WIB', name: 'Western Indonesia Time', abbr: 'WIB', offset: 7, displayName: 'Jakarta (WIB)' },
  SGT: { id: 'SGT', name: 'Singapore Time', abbr: 'SGT', offset: 8, displayName: 'Singapore (SGT)' },
  MYT: { id: 'MYT', name: 'Malaysia Time', abbr: 'MYT', offset: 8, displayName: 'Kuala Lumpur (MYT)' },
  HKT: { id: 'HKT', name: 'Hong Kong Time', abbr: 'HKT', offset: 8, displayName: 'Hong Kong (HKT)' },
  CST_CN: { id: 'CST_CN', name: 'China Standard Time', abbr: 'CST', offset: 8, displayName: 'Beijing (CST)' },
  PHT: { id: 'PHT', name: 'Philippine Time', abbr: 'PHT', offset: 8, displayName: 'Manila (PHT)' },
  MANILA: { id: 'MANILA', name: 'Manila Time', abbr: 'PHT', offset: 8, displayName: 'Manila' },
  KST: { id: 'KST', name: 'Korea Standard Time', abbr: 'KST', offset: 9, displayName: 'Seoul (KST)' },
  JST: { id: 'JST', name: 'Japan Standard Time', abbr: 'JST', offset: 9, displayName: 'Tokyo (JST)' },
  
  // Middle East
  AST_AR: { id: 'AST_AR', name: 'Arabia Standard Time', abbr: 'AST', offset: 3, displayName: 'Riyadh (AST)' },
  GST: { id: 'GST', name: 'Gulf Standard Time', abbr: 'GST', offset: 4, displayName: 'Dubai (GST)' },
  IRST: { id: 'IRST', name: 'Iran Standard Time', abbr: 'IRST', offset: 3.5, displayName: 'Tehran (IRST)' },
  TRT: { id: 'TRT', name: 'Turkey Time', abbr: 'TRT', offset: 3, displayName: 'Istanbul (TRT)' },
  IST_IL: { id: 'IST_IL', name: 'Israel Standard Time', abbr: 'IST', offset: 2, displayName: 'Jerusalem (IST)' },
  
  // Africa
  CAT: { id: 'CAT', name: 'Central Africa Time', abbr: 'CAT', offset: 2, displayName: 'Cairo (CAT)' },
  EAT: { id: 'EAT', name: 'East Africa Time', abbr: 'EAT', offset: 3, displayName: 'Nairobi (EAT)' },
  WAT: { id: 'WAT', name: 'West Africa Time', abbr: 'WAT', offset: 1, displayName: 'Lagos (WAT)' },
  SAST: { id: 'SAST', name: 'South Africa Standard Time', abbr: 'SAST', offset: 2, displayName: 'Johannesburg (SAST)' },
  
  // Oceania
  AEST: { id: 'AEST', name: 'Australian Eastern Standard Time', abbr: 'AEST', offset: 10, displayName: 'Sydney (AEST)' },
  AEDT: { id: 'AEDT', name: 'Australian Eastern Daylight Time', abbr: 'AEDT', offset: 11, displayName: 'Sydney (AEDT)' },
  ACST: { id: 'ACST', name: 'Australian Central Standard Time', abbr: 'ACST', offset: 9.5, displayName: 'Adelaide (ACST)' },
  ACDT: { id: 'ACDT', name: 'Australian Central Daylight Time', abbr: 'ACDT', offset: 10.5, displayName: 'Adelaide (ACDT)' },
  AWST: { id: 'AWST', name: 'Australian Western Standard Time', abbr: 'AWST', offset: 8, displayName: 'Perth (AWST)' },
  NZST: { id: 'NZST', name: 'New Zealand Standard Time', abbr: 'NZST', offset: 12, displayName: 'Auckland (NZST)' },
  NZDT: { id: 'NZDT', name: 'New Zealand Daylight Time', abbr: 'NZDT', offset: 13, displayName: 'Auckland (NZDT)' },
  FJT: { id: 'FJT', name: 'Fiji Time', abbr: 'FJT', offset: 12, displayName: 'Suva (FJT)' },
};

export interface ConversionPair {
  from: string;
  to: string;
  slug: string;
  title: string;
  description: string;
}

// Helper function to generate conversion pairs
function createPair(from: string, to: string): ConversionPair[] {
  const fromTz = TIMEZONES[from];
  const toTz = TIMEZONES[to];
  if (!fromTz || !toTz) return [];
  
  const fromSlug = from.toLowerCase().replace('_', '-');
  const toSlug = to.toLowerCase().replace('_', '-');
  
  return [
    {
      from,
      to,
      slug: `${fromSlug}-to-${toSlug}-converter`,
      title: `${fromTz.abbr} to ${toTz.abbr}`,
      description: `Convert ${fromTz.name} to ${toTz.name}`,
    },
    {
      from: to,
      to: from,
      slug: `${toSlug}-to-${fromSlug}-converter`,
      title: `${toTz.abbr} to ${fromTz.abbr}`,
      description: `Convert ${toTz.name} to ${fromTz.name}`,
    },
  ];
}

export const CONVERSION_PAIRS: ConversionPair[] = [
  // ===== EST (Eastern Standard Time) Conversions =====
  ...createPair('EST', 'IST'),
  ...createPair('EST', 'PST'),
  ...createPair('EST', 'CST'),
  ...createPair('EST', 'GMT'),
  ...createPair('EST', 'UTC'),
  ...createPair('EST', 'PHT'),
  ...createPair('EST', 'MST'),
  ...createPair('EST', 'CET'),
  ...createPair('EST', 'JST'),
  ...createPair('EST', 'AEST'),
  ...createPair('EST', 'SGT'),
  ...createPair('EST', 'HKT'),
  ...createPair('EST', 'KST'),
  ...createPair('EST', 'GST'),
  ...createPair('EST', 'BST'),
  ...createPair('EST', 'EET'),
  ...createPair('EST', 'MSK'),
  ...createPair('EST', 'NZST'),
  ...createPair('EST', 'HST'),
  ...createPair('EST', 'AKST'),
  ...createPair('EST', 'BRT'),
  ...createPair('EST', 'PKT'),
  ...createPair('EST', 'ICT'),
  ...createPair('EST', 'WAT'),
  ...createPair('EST', 'SAST'),
  
  // ===== CST (Central Standard Time) Conversions =====
  ...createPair('CST', 'IST'),
  ...createPair('CST', 'PST'),
  ...createPair('CST', 'GMT'),
  ...createPair('CST', 'UTC'),
  ...createPair('CST', 'CET'),
  ...createPair('CST', 'JST'),
  ...createPair('CST', 'AEST'),
  ...createPair('CST', 'SGT'),
  ...createPair('CST', 'HKT'),
  ...createPair('CST', 'BST'),
  ...createPair('CST', 'MSK'),
  ...createPair('CST', 'NZST'),
  ...createPair('CST', 'HST'),
  ...createPair('CST', 'PKT'),
  
  // ===== PST (Pacific Standard Time) Conversions =====
  ...createPair('PST', 'IST'),
  ...createPair('PST', 'UTC'),
  ...createPair('PST', 'GMT'),
  ...createPair('PST', 'MANILA'),
  ...createPair('PST', 'CET'),
  ...createPair('PST', 'JST'),
  ...createPair('PST', 'AEST'),
  ...createPair('PST', 'SGT'),
  ...createPair('PST', 'HKT'),
  ...createPair('PST', 'KST'),
  ...createPair('PST', 'GST'),
  ...createPair('PST', 'BST'),
  ...createPair('PST', 'MSK'),
  ...createPair('PST', 'NZST'),
  ...createPair('PST', 'HST'),
  ...createPair('PST', 'AKST'),
  ...createPair('PST', 'BRT'),
  ...createPair('PST', 'PKT'),
  ...createPair('PST', 'ICT'),
  ...createPair('PST', 'MST'),
  
  // ===== MST (Mountain Standard Time) Conversions =====
  ...createPair('MST', 'IST'),
  ...createPair('MST', 'GMT'),
  ...createPair('MST', 'UTC'),
  ...createPair('MST', 'CET'),
  ...createPair('MST', 'JST'),
  ...createPair('MST', 'AEST'),
  ...createPair('MST', 'SGT'),
  ...createPair('MST', 'CST'),
  ...createPair('MST', 'PST'),
  
  // ===== IST (India Standard Time) Conversions =====
  ...createPair('IST', 'UTC'),
  ...createPair('IST', 'GMT'),
  ...createPair('IST', 'UK'),
  ...createPair('IST', 'CET'),
  ...createPair('IST', 'BST'),
  ...createPair('IST', 'JST'),
  ...createPair('IST', 'AEST'),
  ...createPair('IST', 'SGT'),
  ...createPair('IST', 'HKT'),
  ...createPair('IST', 'KST'),
  ...createPair('IST', 'GST'),
  ...createPair('IST', 'MSK'),
  ...createPair('IST', 'EET'),
  ...createPair('IST', 'NZST'),
  ...createPair('IST', 'HST'),
  ...createPair('IST', 'AKST'),
  ...createPair('IST', 'BRT'),
  ...createPair('IST', 'PKT'),
  ...createPair('IST', 'ICT'),
  ...createPair('IST', 'WAT'),
  ...createPair('IST', 'SAST'),
  ...createPair('IST', 'EAT'),
  ...createPair('IST', 'PHT'),
  ...createPair('IST', 'NPT'),
  ...createPair('IST', 'IRST'),
  ...createPair('IST', 'TRT'),
  ...createPair('IST', 'AWST'),
  
  // ===== GMT/UTC Conversions =====
  ...createPair('GMT', 'UTC'),
  ...createPair('GMT', 'CET'),
  ...createPair('GMT', 'BST'),
  ...createPair('GMT', 'JST'),
  ...createPair('GMT', 'AEST'),
  ...createPair('GMT', 'SGT'),
  ...createPair('GMT', 'HKT'),
  ...createPair('GMT', 'KST'),
  ...createPair('GMT', 'GST'),
  ...createPair('GMT', 'MSK'),
  ...createPair('GMT', 'EET'),
  ...createPair('GMT', 'NZST'),
  ...createPair('GMT', 'HST'),
  ...createPair('GMT', 'AKST'),
  ...createPair('GMT', 'BRT'),
  ...createPair('GMT', 'PKT'),
  ...createPair('GMT', 'ICT'),
  ...createPair('GMT', 'WAT'),
  ...createPair('GMT', 'SAST'),
  ...createPair('GMT', 'EAT'),
  ...createPair('GMT', 'PHT'),
  ...createPair('UTC', 'CET'),
  ...createPair('UTC', 'JST'),
  ...createPair('UTC', 'AEST'),
  ...createPair('UTC', 'SGT'),
  ...createPair('UTC', 'NZST'),
  ...createPair('UTC', 'HST'),
  ...createPair('UTC', 'BRT'),
  ...createPair('UTC', 'MSK'),
  
  // ===== UK/BST Conversions =====
  ...createPair('UK', 'CET'),
  ...createPair('UK', 'JST'),
  ...createPair('UK', 'AEST'),
  ...createPair('UK', 'SGT'),
  ...createPair('UK', 'HKT'),
  ...createPair('UK', 'PST'),
  ...createPair('UK', 'EST'),
  ...createPair('BST', 'CET'),
  ...createPair('BST', 'JST'),
  ...createPair('BST', 'AEST'),
  ...createPair('BST', 'SGT'),
  ...createPair('BST', 'HKT'),
  
  // ===== CET (Central European Time) Conversions =====
  ...createPair('CET', 'JST'),
  ...createPair('CET', 'AEST'),
  ...createPair('CET', 'SGT'),
  ...createPair('CET', 'HKT'),
  ...createPair('CET', 'KST'),
  ...createPair('CET', 'GST'),
  ...createPair('CET', 'MSK'),
  ...createPair('CET', 'EET'),
  ...createPair('CET', 'NZST'),
  ...createPair('CET', 'BRT'),
  ...createPair('CET', 'PKT'),
  ...createPair('CET', 'WAT'),
  ...createPair('CET', 'SAST'),
  
  // ===== EET (Eastern European Time) Conversions =====
  ...createPair('EET', 'JST'),
  ...createPair('EET', 'AEST'),
  ...createPair('EET', 'SGT'),
  ...createPair('EET', 'HKT'),
  ...createPair('EET', 'PST'),
  ...createPair('EET', 'MSK'),
  ...createPair('EET', 'GST'),
  
  // ===== MSK (Moscow Time) Conversions =====
  ...createPair('MSK', 'JST'),
  ...createPair('MSK', 'AEST'),
  ...createPair('MSK', 'SGT'),
  ...createPair('MSK', 'HKT'),
  ...createPair('MSK', 'PST'),
  ...createPair('MSK', 'NZST'),
  
  // ===== JST (Japan Standard Time) Conversions =====
  ...createPair('JST', 'AEST'),
  ...createPair('JST', 'SGT'),
  ...createPair('JST', 'HKT'),
  ...createPair('JST', 'KST'),
  ...createPair('JST', 'GST'),
  ...createPair('JST', 'NZST'),
  ...createPair('JST', 'HST'),
  ...createPair('JST', 'AKST'),
  ...createPair('JST', 'PHT'),
  ...createPair('JST', 'ICT'),
  ...createPair('JST', 'PKT'),
  
  // ===== KST (Korea Standard Time) Conversions =====
  ...createPair('KST', 'AEST'),
  ...createPair('KST', 'SGT'),
  ...createPair('KST', 'HKT'),
  ...createPair('KST', 'GST'),
  ...createPair('KST', 'NZST'),
  ...createPair('KST', 'PHT'),
  ...createPair('KST', 'ICT'),
  
  // ===== SGT (Singapore Time) Conversions =====
  ...createPair('SGT', 'AEST'),
  ...createPair('SGT', 'HKT'),
  ...createPair('SGT', 'KST'),
  ...createPair('SGT', 'JST'),
  ...createPair('SGT', 'GST'),
  ...createPair('SGT', 'NZST'),
  ...createPair('SGT', 'PHT'),
  ...createPair('SGT', 'ICT'),
  ...createPair('SGT', 'MYT'),
  
  // ===== HKT (Hong Kong Time) Conversions =====
  ...createPair('HKT', 'AEST'),
  ...createPair('HKT', 'KST'),
  ...createPair('HKT', 'JST'),
  ...createPair('HKT', 'NZST'),
  ...createPair('HKT', 'PHT'),
  ...createPair('HKT', 'GST'),
  
  // ===== CST_CN (China Standard Time) Conversions =====
  ...createPair('CST_CN', 'EST'),
  ...createPair('CST_CN', 'PST'),
  ...createPair('CST_CN', 'GMT'),
  ...createPair('CST_CN', 'IST'),
  ...createPair('CST_CN', 'JST'),
  ...createPair('CST_CN', 'AEST'),
  ...createPair('CST_CN', 'CET'),
  ...createPair('CST_CN', 'SGT'),
  ...createPair('CST_CN', 'KST'),
  
  // ===== PHT (Philippine Time) Conversions =====
  ...createPair('PHT', 'AEST'),
  ...createPair('PHT', 'KST'),
  ...createPair('PHT', 'GST'),
  ...createPair('PHT', 'NZST'),
  ...createPair('PHT', 'ICT'),
  ...createPair('PHT', 'GMT'),
  ...createPair('PHT', 'CET'),
  
  // ===== AEST (Australian Eastern Standard Time) Conversions =====
  ...createPair('AEST', 'NZST'),
  ...createPair('AEST', 'HST'),
  ...createPair('AEST', 'AKST'),
  ...createPair('AEST', 'GST'),
  ...createPair('AEST', 'PKT'),
  ...createPair('AEST', 'ICT'),
  ...createPair('AEST', 'WAT'),
  ...createPair('AEST', 'SAST'),
  ...createPair('AEST', 'AWST'),
  ...createPair('AEST', 'ACST'),
  
  // ===== AWST (Australian Western Standard Time) Conversions =====
  ...createPair('AWST', 'EST'),
  ...createPair('AWST', 'PST'),
  ...createPair('AWST', 'GMT'),
  ...createPair('AWST', 'JST'),
  ...createPair('AWST', 'SGT'),
  ...createPair('AWST', 'NZST'),
  ...createPair('AWST', 'CET'),
  
  // ===== ACST (Australian Central Standard Time) Conversions =====
  ...createPair('ACST', 'EST'),
  ...createPair('ACST', 'PST'),
  ...createPair('ACST', 'GMT'),
  ...createPair('ACST', 'IST'),
  ...createPair('ACST', 'JST'),
  ...createPair('ACST', 'SGT'),
  ...createPair('ACST', 'NZST'),
  
  // ===== NZST (New Zealand Standard Time) Conversions =====
  ...createPair('NZST', 'HST'),
  ...createPair('NZST', 'AKST'),
  ...createPair('NZST', 'GST'),
  ...createPair('NZST', 'PKT'),
  ...createPair('NZST', 'ICT'),
  ...createPair('NZST', 'WAT'),
  ...createPair('NZST', 'SAST'),
  ...createPair('NZST', 'BRT'),
  
  // ===== GST (Gulf Standard Time - Dubai) Conversions =====
  ...createPair('GST', 'AEST'),
  ...createPair('GST', 'PKT'),
  ...createPair('GST', 'ICT'),
  ...createPair('GST', 'WAT'),
  ...createPair('GST', 'SAST'),
  ...createPair('GST', 'BRT'),
  ...createPair('GST', 'HST'),
  
  // ===== AST_AR (Arabia Standard Time) Conversions =====
  ...createPair('AST_AR', 'EST'),
  ...createPair('AST_AR', 'PST'),
  ...createPair('AST_AR', 'GMT'),
  ...createPair('AST_AR', 'IST'),
  ...createPair('AST_AR', 'JST'),
  ...createPair('AST_AR', 'AEST'),
  ...createPair('AST_AR', 'SGT'),
  ...createPair('AST_AR', 'CET'),
  
  // ===== TRT (Turkey Time) Conversions =====
  ...createPair('TRT', 'EST'),
  ...createPair('TRT', 'PST'),
  ...createPair('TRT', 'GMT'),
  ...createPair('TRT', 'JST'),
  ...createPair('TRT', 'AEST'),
  ...createPair('TRT', 'SGT'),
  ...createPair('TRT', 'CET'),
  ...createPair('TRT', 'MSK'),
  
  // ===== IRST (Iran Standard Time) Conversions =====
  ...createPair('IRST', 'EST'),
  ...createPair('IRST', 'PST'),
  ...createPair('IRST', 'GMT'),
  ...createPair('IRST', 'UTC'),
  ...createPair('IRST', 'JST'),
  ...createPair('IRST', 'AEST'),
  ...createPair('IRST', 'CET'),
  
  // ===== PKT (Pakistan Standard Time) Conversions =====
  ...createPair('PKT', 'GMT'),
  ...createPair('PKT', 'UTC'),
  ...createPair('PKT', 'CET'),
  ...createPair('PKT', 'AEST'),
  ...createPair('PKT', 'SGT'),
  ...createPair('PKT', 'JST'),
  ...createPair('PKT', 'NZST'),
  
  // ===== NPT (Nepal Time) Conversions =====
  ...createPair('NPT', 'EST'),
  ...createPair('NPT', 'PST'),
  ...createPair('NPT', 'GMT'),
  ...createPair('NPT', 'UTC'),
  ...createPair('NPT', 'JST'),
  ...createPair('NPT', 'AEST'),
  ...createPair('NPT', 'SGT'),
  ...createPair('NPT', 'CET'),
  
  // ===== BST_BD (Bangladesh Standard Time) Conversions =====
  ...createPair('BST_BD', 'EST'),
  ...createPair('BST_BD', 'PST'),
  ...createPair('BST_BD', 'GMT'),
  ...createPair('BST_BD', 'IST'),
  ...createPair('BST_BD', 'JST'),
  ...createPair('BST_BD', 'AEST'),
  ...createPair('BST_BD', 'SGT'),
  ...createPair('BST_BD', 'CET'),
  
  // ===== ICT (Indochina Time - Bangkok) Conversions =====
  ...createPair('ICT', 'GMT'),
  ...createPair('ICT', 'UTC'),
  ...createPair('ICT', 'CET'),
  ...createPair('ICT', 'AEST'),
  ...createPair('ICT', 'SGT'),
  ...createPair('ICT', 'JST'),
  ...createPair('ICT', 'HKT'),
  ...createPair('ICT', 'NZST'),
  
  // ===== WIB (Western Indonesia Time) Conversions =====
  ...createPair('WIB', 'EST'),
  ...createPair('WIB', 'PST'),
  ...createPair('WIB', 'GMT'),
  ...createPair('WIB', 'IST'),
  ...createPair('WIB', 'JST'),
  ...createPair('WIB', 'AEST'),
  ...createPair('WIB', 'SGT'),
  ...createPair('WIB', 'CET'),
  
  // ===== MYT (Malaysia Time) Conversions =====
  ...createPair('MYT', 'EST'),
  ...createPair('MYT', 'PST'),
  ...createPair('MYT', 'GMT'),
  ...createPair('MYT', 'IST'),
  ...createPair('MYT', 'JST'),
  ...createPair('MYT', 'AEST'),
  ...createPair('MYT', 'CET'),
  ...createPair('MYT', 'NZST'),
  
  // ===== BRT (Brasilia Time) Conversions =====
  ...createPair('BRT', 'GMT'),
  ...createPair('BRT', 'CET'),
  ...createPair('BRT', 'JST'),
  ...createPair('BRT', 'AEST'),
  ...createPair('BRT', 'SGT'),
  ...createPair('BRT', 'ART'),
  ...createPair('BRT', 'CLT'),
  
  // ===== ART (Argentina Time) Conversions =====
  ...createPair('ART', 'EST'),
  ...createPair('ART', 'PST'),
  ...createPair('ART', 'GMT'),
  ...createPair('ART', 'IST'),
  ...createPair('ART', 'CET'),
  ...createPair('ART', 'JST'),
  ...createPair('ART', 'AEST'),
  
  // ===== CLT (Chile Time) Conversions =====
  ...createPair('CLT', 'EST'),
  ...createPair('CLT', 'PST'),
  ...createPair('CLT', 'GMT'),
  ...createPair('CLT', 'IST'),
  ...createPair('CLT', 'CET'),
  ...createPair('CLT', 'JST'),
  ...createPair('CLT', 'AEST'),
  
  // ===== COT (Colombia Time) Conversions =====
  ...createPair('COT', 'EST'),
  ...createPair('COT', 'PST'),
  ...createPair('COT', 'GMT'),
  ...createPair('COT', 'IST'),
  ...createPair('COT', 'CET'),
  ...createPair('COT', 'JST'),
  ...createPair('COT', 'AEST'),
  ...createPair('COT', 'BRT'),
  
  // ===== PET (Peru Time) Conversions =====
  ...createPair('PET', 'EST'),
  ...createPair('PET', 'PST'),
  ...createPair('PET', 'GMT'),
  ...createPair('PET', 'IST'),
  ...createPair('PET', 'CET'),
  ...createPair('PET', 'JST'),
  ...createPair('PET', 'AEST'),
  ...createPair('PET', 'BRT'),
  
  // ===== VET (Venezuela Time) Conversions =====
  ...createPair('VET', 'EST'),
  ...createPair('VET', 'PST'),
  ...createPair('VET', 'GMT'),
  ...createPair('VET', 'IST'),
  ...createPair('VET', 'CET'),
  ...createPair('VET', 'BRT'),
  
  // ===== HST (Hawaii Standard Time) Conversions =====
  ...createPair('HST', 'GMT'),
  ...createPair('HST', 'UTC'),
  ...createPair('HST', 'JST'),
  ...createPair('HST', 'SGT'),
  ...createPair('HST', 'HKT'),
  ...createPair('HST', 'KST'),
  ...createPair('HST', 'PHT'),
  ...createPair('HST', 'ICT'),
  
  // ===== AKST (Alaska Standard Time) Conversions =====
  ...createPair('AKST', 'GMT'),
  ...createPair('AKST', 'UTC'),
  ...createPair('AKST', 'IST'),
  ...createPair('AKST', 'JST'),
  ...createPair('AKST', 'SGT'),
  ...createPair('AKST', 'CET'),
  ...createPair('AKST', 'CST'),
  ...createPair('AKST', 'MST'),
  
  // ===== WAT (West Africa Time) Conversions =====
  ...createPair('WAT', 'GMT'),
  ...createPair('WAT', 'UTC'),
  ...createPair('WAT', 'CET'),
  ...createPair('WAT', 'JST'),
  ...createPair('WAT', 'AEST'),
  ...createPair('WAT', 'SGT'),
  ...createPair('WAT', 'EAT'),
  ...createPair('WAT', 'SAST'),
  
  // ===== EAT (East Africa Time) Conversions =====
  ...createPair('EAT', 'EST'),
  ...createPair('EAT', 'PST'),
  ...createPair('EAT', 'GMT'),
  ...createPair('EAT', 'UTC'),
  ...createPair('EAT', 'CET'),
  ...createPair('EAT', 'JST'),
  ...createPair('EAT', 'AEST'),
  ...createPair('EAT', 'SGT'),
  ...createPair('EAT', 'SAST'),
  
  // ===== SAST (South Africa Standard Time) Conversions =====
  ...createPair('SAST', 'EST'),
  ...createPair('SAST', 'PST'),
  ...createPair('SAST', 'GMT'),
  ...createPair('SAST', 'UTC'),
  ...createPair('SAST', 'CET'),
  ...createPair('SAST', 'JST'),
  ...createPair('SAST', 'AEST'),
  ...createPair('SAST', 'SGT'),
  ...createPair('SAST', 'EAT'),
  
  // ===== CAT (Central Africa Time) Conversions =====
  ...createPair('CAT', 'EST'),
  ...createPair('CAT', 'PST'),
  ...createPair('CAT', 'GMT'),
  ...createPair('CAT', 'IST'),
  ...createPair('CAT', 'CET'),
  ...createPair('CAT', 'JST'),
  ...createPair('CAT', 'AEST'),
  ...createPair('CAT', 'SGT'),
  
  // ===== FJT (Fiji Time) Conversions =====
  ...createPair('FJT', 'EST'),
  ...createPair('FJT', 'PST'),
  ...createPair('FJT', 'GMT'),
  ...createPair('FJT', 'IST'),
  ...createPair('FJT', 'JST'),
  ...createPair('FJT', 'AEST'),
  ...createPair('FJT', 'SGT'),
  ...createPair('FJT', 'NZST'),
  
  // ===== WET (Western European Time) Conversions =====
  ...createPair('WET', 'EST'),
  ...createPair('WET', 'PST'),
  ...createPair('WET', 'IST'),
  ...createPair('WET', 'JST'),
  ...createPair('WET', 'AEST'),
  ...createPair('WET', 'CET'),
  
  // ===== PT / Central Time Conversions =====
  ...createPair('PT', 'CENTRAL'),
  ...createPair('PT', 'EST'),
  ...createPair('PT', 'IST'),
  ...createPair('PT', 'GMT'),
  ...createPair('PT', 'JST'),
  
  // ===== Daylight Time Conversions (EDT, PDT, CDT, MDT) =====
  ...createPair('EDT', 'IST'),
  ...createPair('EDT', 'PDT'),
  ...createPair('EDT', 'GMT'),
  ...createPair('EDT', 'BST'),
  ...createPair('EDT', 'CEST'),
  ...createPair('EDT', 'JST'),
  ...createPair('EDT', 'AEST'),
  ...createPair('PDT', 'IST'),
  ...createPair('PDT', 'GMT'),
  ...createPair('PDT', 'BST'),
  ...createPair('PDT', 'JST'),
  ...createPair('PDT', 'AEST'),
  ...createPair('CDT', 'IST'),
  ...createPair('CDT', 'GMT'),
  ...createPair('CDT', 'BST'),
  ...createPair('CDT', 'JST'),
  ...createPair('CDT', 'EDT'),
  ...createPair('CDT', 'PDT'),
  ...createPair('MDT', 'IST'),
  ...createPair('MDT', 'GMT'),
  ...createPair('MDT', 'JST'),
  ...createPair('MDT', 'EDT'),
  ...createPair('MDT', 'PDT'),
  ...createPair('AEDT', 'EST'),
  ...createPair('AEDT', 'PST'),
  ...createPair('AEDT', 'GMT'),
  ...createPair('AEDT', 'IST'),
  ...createPair('AEDT', 'JST'),
  ...createPair('AEDT', 'NZDT'),
  ...createPair('NZDT', 'EST'),
  ...createPair('NZDT', 'PST'),
  ...createPair('NZDT', 'GMT'),
  ...createPair('NZDT', 'IST'),
  ...createPair('NZDT', 'JST'),
  ...createPair('NZDT', 'AEST'),
  ...createPair('AKDT', 'EST'),
  ...createPair('AKDT', 'PST'),
  ...createPair('AKDT', 'GMT'),
  ...createPair('AKDT', 'IST'),
  ...createPair('CEST', 'EST'),
  ...createPair('CEST', 'PST'),
  ...createPair('CEST', 'IST'),
  ...createPair('CEST', 'JST'),
  ...createPair('CEST', 'AEST'),
  ...createPair('EEST', 'EST'),
  ...createPair('EEST', 'PST'),
  ...createPair('EEST', 'IST'),
  ...createPair('EEST', 'JST'),
  ...createPair('WEST', 'EST'),
  ...createPair('WEST', 'PST'),
  ...createPair('WEST', 'IST'),
  ...createPair('ACDT', 'EST'),
  ...createPair('ACDT', 'PST'),
  ...createPair('ACDT', 'GMT'),
  ...createPair('ACDT', 'IST'),
  ...createPair('ACDT', 'JST'),
];

// Remove duplicates based on slug
const uniquePairs = CONVERSION_PAIRS.filter((pair, index, self) =>
  index === self.findIndex(p => p.slug === pair.slug)
);

// Replace the original array with deduplicated version
export { uniquePairs as UNIQUE_CONVERSION_PAIRS };

export function convertTime(
  hours: number,
  minutes: number,
  fromTz: TimezoneInfo,
  toTz: TimezoneInfo
): { hours: number; minutes: number; dayOffset: number } {
  // Convert to UTC first
  const totalMinutesFrom = hours * 60 + minutes;
  const utcMinutes = totalMinutesFrom - getCurrentOffset(fromTz.id) * 60;
  
  // Convert to target timezone
  let targetMinutes = utcMinutes + getCurrentOffset(toTz.id) * 60;
  
  // Handle day overflow/underflow
  let dayOffset = 0;
  while (targetMinutes < 0) {
    targetMinutes += 24 * 60;
    dayOffset -= 1;
  }
  while (targetMinutes >= 24 * 60) {
    targetMinutes -= 24 * 60;
    dayOffset += 1;
  }
  
  return {
    hours: Math.floor(targetMinutes / 60),
    minutes: Math.round(targetMinutes % 60),
    dayOffset,
  };
}

export function formatTime(hours: number, minutes: number, format24: boolean = false): string {
  if (format24) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export function getConversionBySlug(slug: string): ConversionPair | undefined {
  return CONVERSION_PAIRS.find(pair => pair.slug === slug);
}
